import type { AppConfig } from 'features/app-config/interfaces';
import { AppConfigService } from 'features/app-config/services';
import {
  fetchAppConfig,
  appConfigRetrievedSuccessful,
} from 'features/app-config/store';
import { useAppDispatch } from 'features/app/hooks';
import { useEmergencyFlag } from 'features/emergency/hooks';
import { setEmergencyPhoneNumbers } from 'features/emergency/store';
import { handleError } from 'features/notification/store';
import {
  sendAnalyticsError,
  sendAnalyticsInfo,
} from 'features/analytics/helpers';
import { RetryManager } from 'features/utils/helpers';

export const useFetchAppConfig = () => {
  const dispatch = useAppDispatch();
  const { isEmergencyEnabled } = useEmergencyFlag();

  // Try to fetch and process config with retries
  const fetchConfig = async (): Promise<AppConfig> => {
    const retryManager = RetryManager.withoutJitter(1000, 3);

    return await retryManager.execute(
      async (_context) => {
        // Fetch config
        const config = await dispatch(fetchAppConfig()).unwrap();

        // Handle emergency settings if needed
        if (isEmergencyEnabled) {
          if (config.EmergencyExt && config.Intrado911ConnectionString) {
            dispatch(setEmergencyPhoneNumbers(config));
          } else {
            throw new Error('Missing emergency numbers');
          }
        }

        if (config.ActiveLanguages.length === 0) {
          throw new Error('No active languages');
        }

        // Save config and log success
        AppConfigService.setAppConfig(config);
        dispatch(appConfigRetrievedSuccessful());
        dispatch(
          sendAnalyticsInfo({ message: 'Config loaded successfully', config })
        );

        return config;
      },
      {
        onAttempt: (context) => {
          if (context.isRetry) {
            dispatch(
              sendAnalyticsError({
                message: `Config fetch attempt ${context.attemptNumber}/${context.maxAttempts} failed. Retrying in ${context.backoffDelay}ms`,
                retryCount: context.attemptNumber,
                delayMs: context.backoffDelay,
              })
            );
          }
        },
        onFailure: (_context) => {
          dispatch(
            handleError({
              error: new Error('All config fetch attempts failed'),
              methodName: 'fetchConfig',
              message: 'Failed to load config',
            })
          );
        },
      }
    );
  };

  return { fetchConfig };
};
